import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Building2, ArrowLeft, File, Award, FileText, CalendarClock, FileEdit, ShieldCheck, Briefcase, Gavel, Info, BookOpen, DollarSign, Globe } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { LegalAssistanceSheet } from "@/components/legal/LegalAssistanceSheet";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getNoticeTypeDescription, getNoticeTypeCategory, getNoticeTypeDetailedDescription, getNoticeSubtypeId, getEnhancedNoticeTypeDescription, getDocumentType } from "@/utils/procedureTypeUtils";
import { getNoticeSubtypeById, getDocumentTypeDescription } from "@/utils/noticeSubtypeData";
import { getProcedureTypeDescription, getContractNatureDescription } from "@/utils/procurementFieldDescriptions";
import { formatCurrency } from "@/utils/dateFormatters";
import { useTranslation } from "react-i18next";

interface TenderHeaderProps {
  tenderId: string;
  tenderData: any;
}

export const TenderHeader = ({
  tenderId,
  tenderData
}: TenderHeaderProps) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("offerManagement");
  const currentLanguage = i18n.language;
  
  const getTitle = () => {
    if (!tenderData) return "Untitled Tender";

    // For saved tenders with tender_data structure
    if (tenderData.tender_data) {
      // Try match_reasons first
      if (tenderData.tender_data.match_reasons?.["notice-title"]?.eng) {
        return tenderData.tender_data.match_reasons["notice-title"].eng;
      }

      // Try notice_title in match_reasons with array format
      if (tenderData.tender_data.match_reasons?.notice_title?.eng?.[0]) {
        return tenderData.tender_data.match_reasons.notice_title.eng[0];
      }

      // Try direct notice-title
      if (tenderData.tender_data["notice-title"]?.eng) {
        return tenderData.tender_data["notice-title"].eng;
      }

      // Try title field directly
      if (tenderData.tender_data.title) {
        return tenderData.tender_data.title;
      }
    }

    // Direct properties on tenderData
    if (tenderData.title) {
      return tenderData.title;
    }
    if (tenderData["notice-title"]?.eng) {
      return tenderData["notice-title"].eng;
    }
    if (tenderData.match_reasons?.["notice-title"]?.eng) {
      return tenderData.match_reasons["notice-title"].eng;
    }
    if (tenderData.match_reasons?.notice_title?.eng?.[0]) {
      return tenderData.match_reasons.notice_title.eng[0];
    }
    if (tenderData.TI?.eng) {
      return tenderData.TI.eng;
    }
    return "Untitled Tender";
  };
  
  const getOrganization = () => {
    if (!tenderData) return "Unknown Organization";

    // For nested tender_data structure
    if (tenderData.tender_data) {
      // Check for organisation-name-buyer
      if (tenderData.tender_data["organisation-name-buyer"]) {
        const orgNameBuyer = tenderData.tender_data["organisation-name-buyer"];
        if (typeof orgNameBuyer === 'object' && !Array.isArray(orgNameBuyer)) {
          // If it's an object with language keys
          const langs = Object.keys(orgNameBuyer);
          if (langs.length > 0) {
            const orgValue = orgNameBuyer[langs[0]];
            if (Array.isArray(orgValue) && orgValue.length > 0) {
              return orgValue[0];
            } else if (typeof orgValue === 'string') {
              return orgValue;
            }
          }
        } else if (Array.isArray(orgNameBuyer) && orgNameBuyer.length > 0) {
          return orgNameBuyer[0];
        } else if (typeof orgNameBuyer === 'string') {
          return orgNameBuyer;
        }
      }

      // Check for buyer-name in match_reasons
      if (tenderData.tender_data.match_reasons?.["buyer-name"]?.eng?.[0]) {
        return tenderData.tender_data.match_reasons["buyer-name"].eng[0];
      }

      // Try direct buyer-name field
      if (tenderData.tender_data["buyer-name"]) {
        const buyerNameObj = tenderData.tender_data["buyer-name"];
        if (typeof buyerNameObj === 'object' && !Array.isArray(buyerNameObj)) {
          if (buyerNameObj.eng) {
            if (Array.isArray(buyerNameObj.eng) && buyerNameObj.eng.length > 0) {
              return buyerNameObj.eng[0];
            }
            return buyerNameObj.eng;
          }
          const langs = Object.keys(buyerNameObj);
          if (langs.length > 0) {
            const firstLang = buyerNameObj[langs[0]];
            if (Array.isArray(firstLang) && firstLang.length > 0) {
              return firstLang[0];
            } else if (typeof firstLang === 'string') {
              return firstLang;
            }
          }
        } else if (typeof buyerNameObj === 'string') {
          return buyerNameObj;
        }
      }

      // Try AU field
      if (tenderData.tender_data.AU) {
        if (typeof tenderData.tender_data.AU === 'object') {
          const langs = Object.keys(tenderData.tender_data.AU);
          if (langs.length > 0 && Array.isArray(tenderData.tender_data.AU[langs[0]])) {
            return tenderData.tender_data.AU[langs[0]][0] || "Unknown Organization";
          }
        }
        return tenderData.tender_data.AU;
      }

      // Try organization_name field
      if (tenderData.tender_data.organization_name) {
        return tenderData.tender_data.organization_name;
      }
    }

    // Direct properties on tenderData
    if (tenderData.organization_name) {
      return tenderData.organization_name;
    }
    if (tenderData.organization) {
      return tenderData.organization;
    }
    if (tenderData.buyer_name) {
      return tenderData.buyer_name;
    }
    if (tenderData.CAE_NAME) {
      return tenderData.CAE_NAME;
    }

    // Check for organisation-name-buyer
    if (tenderData["organisation-name-buyer"]) {
      const orgNameBuyer = tenderData["organisation-name-buyer"];
      if (typeof orgNameBuyer === 'object' && !Array.isArray(orgNameBuyer)) {
        const langs = Object.keys(orgNameBuyer);
        if (langs.length > 0) {
          const orgValue = orgNameBuyer[langs[0]];
          if (Array.isArray(orgValue) && orgValue.length > 0) {
            return orgValue[0];
          } else if (typeof orgValue === 'string') {
            return orgValue;
          }
        }
      } else if (Array.isArray(orgNameBuyer) && orgNameBuyer.length > 0) {
        return orgNameBuyer[0];
      } else if (typeof orgNameBuyer === 'string') {
        return orgNameBuyer;
      }
    }

    // Check match_reasons
    if (tenderData.match_reasons) {
      if (tenderData.match_reasons["buyer-name"]) {
        const buyerName = tenderData.match_reasons["buyer-name"];
        if (typeof buyerName === 'object' && !Array.isArray(buyerName)) {
          if (buyerName.eng) {
            if (Array.isArray(buyerName.eng) && buyerName.eng.length > 0) {
              return buyerName.eng[0];
            }
            return buyerName.eng;
          }
          const langs = Object.keys(buyerName);
          if (langs.length > 0) {
            const firstLangValue = buyerName[langs[0]];
            if (Array.isArray(firstLangValue) && firstLangValue.length > 0) {
              return firstLangValue[0];
            }
            if (firstLangValue) {
              return firstLangValue;
            }
          }
        }
        if (Array.isArray(buyerName) && buyerName.length > 0) {
          return buyerName[0];
        }
        if (typeof buyerName === 'string') {
          return buyerName;
        }
      }
    }

    // Try AU field directly
    if (tenderData.AU) {
      if (typeof tenderData.AU === 'object') {
        const langs = Object.keys(tenderData.AU);
        if (langs.length > 0 && Array.isArray(tenderData.AU[langs[0]])) {
          return tenderData.AU[langs[0]][0] || "Unknown Organization";
        }
      }
      return tenderData.AU;
    }
    if (tenderData["buyer-name"]) {
      const buyerName = tenderData["buyer-name"];
      if (typeof buyerName === 'object' && !Array.isArray(buyerName)) {
        if (buyerName.eng) {
          if (Array.isArray(buyerName.eng) && buyerName.eng.length > 0) {
            return buyerName.eng[0];
          }
          return buyerName.eng;
        }
        const langs = Object.keys(buyerName);
        if (langs.length > 0) {
          const firstLangValue = buyerName[langs[0]];
          if (Array.isArray(firstLangValue) && firstLangValue.length > 0) {
            return firstLangValue[0];
          }
          if (firstLangValue) {
            return firstLangValue;
          }
        }
      }
      if (Array.isArray(buyerName) && buyerName.length > 0) {
        return buyerName[0];
      }
      if (typeof buyerName === 'string') {
        return buyerName;
      }
    }
    return "Unknown Organization";
  };
  
  const getNoticeType = () => {
    if (!tenderData) return null;

    // For saved tenders with tender_data structure
    if (tenderData.tender_data && tenderData.tender_data["notice-type"]) {
      return tenderData.tender_data["notice-type"];
    }

    // Direct properties on tenderData
    if (tenderData["notice-type"]) {
      return tenderData["notice-type"];
    }
    return null;
  };
  const getTotalValue = () => {
    if (!tenderData) return null;

    // For saved tenders with tender_data structure
    if (tenderData.tender_data) {
      if (tenderData.tender_data["total-value"]) {
        const value = tenderData.tender_data["total-value"];
        const currency = tenderData.tender_data["total-value-cur"] || "EUR";
        return {
          value,
          currency
        };
      }
      if (tenderData.tender_data.TV) {
        const value = tenderData.tender_data.TV;
        const currency = tenderData.tender_data.TV_CUR || "EUR";
        return {
          value,
          currency
        };
      }
      if (tenderData.tender_data["estimated-value"]) {
        const value = tenderData.tender_data["estimated-value"];
        const currency = tenderData.tender_data["estimated-value-cur"] || "EUR";
        return {
          value,
          currency
        };
      }
    }

    // Direct properties on tenderData
    if (tenderData["total-value"]) {
      const value = tenderData["total-value"];
      const currency = tenderData["total-value-cur"] || "EUR";
      return {
        value,
        currency
      };
    }
    if (tenderData.TV) {
      const value = tenderData.TV;
      const currency = tenderData.TV_CUR || "EUR";
      return {
        value,
        currency
      };
    }
    if (tenderData["estimated-value"]) {
      const value = tenderData["estimated-value"];
      const currency = tenderData["estimated-value-cur"] || "EUR";
      return {
        value,
        currency
      };
    }
    return null;
  };
  const getProcedureType = () => {
    if (!tenderData) return null;

    // For saved tenders with tender_data structure
    if (tenderData.tender_data) {
      if (tenderData.tender_data["procedure-type"]) {
        return tenderData.tender_data["procedure-type"];
      }
      if (tenderData.tender_data.PR) {
        return tenderData.tender_data.PR;
      }
      if (tenderData.tender_data["BT-105-Procedure"]) {
        return tenderData.tender_data["BT-105-Procedure"];
      }
    }

    // Direct properties on tenderData
    if (tenderData["procedure-type"]) {
      return tenderData["procedure-type"];
    }
    if (tenderData.PR) {
      return tenderData.PR;
    }
    if (tenderData["BT-105-Procedure"]) {
      return tenderData["BT-105-Procedure"];
    }
    return null;
  };
  const getContractNature = () => {
    if (!tenderData) return null;

    // For saved tenders with tender_data structure
    if (tenderData.tender_data) {
      if (tenderData.tender_data["contract-nature"]) {
        return tenderData.tender_data["contract-nature"];
      }
      if (tenderData.tender_data.NC) {
        return tenderData.tender_data.NC;
      }
    }

    // Direct properties on tenderData
    if (tenderData["contract-nature"]) {
      return tenderData["contract-nature"];
    }
    if (tenderData.NC) {
      return tenderData.NC;
    }
    return null;
  };
  const getNoticeTypeIcon = (noticeType: string | null) => {
    if (!noticeType) return <FileText className="h-4 w-4" />;
    const category = getNoticeTypeCategory(noticeType);
    switch (category) {
      case 'award':
        return <Award className="h-4 w-4" />;
      case 'contract':
        return <FileText className="h-4 w-4" />;
      case 'prior':
        return <CalendarClock className="h-4 w-4" />;
      case 'change':
        return <FileEdit className="h-4 w-4" />;
      default:
        // More specific icons based on notice type
        if (noticeType === 'veat') return <ShieldCheck className="h-4 w-4" />;
        if (noticeType === 'subco') return <Briefcase className="h-4 w-4" />;
        if (noticeType === 'cn-desg' || noticeType === 'can-desg') return <Gavel className="h-4 w-4" />;
        return <FileText className="h-4 w-4" />;
    }
  };
  const noticeType = getNoticeType();
  const noticeTypeDescription = noticeType ? getNoticeTypeDescription(noticeType) : null;
  const noticeTypeDetailedDescription = noticeType ? getNoticeTypeDetailedDescription(noticeType) : null;
  const enhancedDescription = getEnhancedNoticeTypeDescription(tenderData);
  const noticeSubtypeId = getNoticeSubtypeId(tenderData);
  const noticeSubtype = noticeSubtypeId ? getNoticeSubtypeById(noticeSubtypeId) : null;
  const documentTypeId = getDocumentType(tenderData);
  const documentTypeDesc = documentTypeId ? getDocumentTypeDescription(documentTypeId) : null;
  const totalValue = getTotalValue();
  const procedureType = getProcedureType();
  const procedureTypeDesc = procedureType ? getProcedureTypeDescription(procedureType) : null;
  const contractNature = getContractNature();
  const contractNatureDesc = contractNature ? getContractNatureDescription(contractNature) : null;
  return <div className="mb-6 border-b pb-6">
      <div className="flex items-center justify-between gap-4 mb-4">
        <Button variant="outline" size="sm" className="flex items-center gap-2" onClick={() => navigate("/tender-management")}>
          <ArrowLeft className="h-4 w-4" />
          {t("tenderHeader.back")}
        </Button>
        <div className="flex gap-2">
          <LegalAssistanceSheet tenderId={tenderId} />
        </div>
      </div>
      <div>
        <div className="flex items-center gap-2 mb-2">
          <h1 className="text-2xl font-bold">{getTitle()}</h1>
          
          {totalValue && (
            <Badge variant="outline" className="ml-2">
              <DollarSign className="h-4 w-4 mr-1" />
              <span>{formatCurrency(totalValue.value, totalValue.currency)}</span>
            </Badge>
          )}
          
          {noticeType && (
            <Badge variant="outline" className="ml-2">
              {getNoticeTypeIcon(noticeType)}
              <span className="ml-1">{noticeTypeDescription || noticeType}</span>
            </Badge>
          )}
          
          {procedureType && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="inline-block">
                    <Badge variant="outline" className="ml-2 cursor-help">
                      <FileText className="h-4 w-4 mr-1" />
                      <span>{procedureType}</span>
                    </Badge>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="p-3">
                  <p className="font-medium">Procedure Type</p>
                  {procedureTypeDesc && <p className="text-sm mt-1">{procedureTypeDesc}</p>}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Building2 className="h-4 w-4" />
          <span>{getOrganization()}</span>
        </div>
      </div>
    </div>;
};
